import { Configuration, StreamingApi, WorkItemContext } from "../clients/ogg";
import { ErrorActionButton, IWebAppErrorContext } from "../components/Error";
import { IAuthenticationProvider, IProjectContextProvider, IToastProvider, IWorkItemManager, Rating, Suggestion } from "../types";
import { StreamingSession } from "./streamingSession.service";

export interface ValidationResult {
    isValid: boolean
    error?: IWebAppErrorContext
}

type onStreamUpdate = (nextMesage: string) => void

class OggService {
    private workItemManager: IWorkItemManager;
    private streamingApi: StreamingApi;
    private projectContextProvider: IProjectContextProvider;
    private authProvider: IAuthenticationProvider;
    private validate?: () => Promise<ValidationResult>;

    public constructor(
        workItemManager: IWorkItemManager,
        apiConfig: Configuration,
        projectContextProvider: IProjectContextProvider,
        authProvider: IAuthenticationProvider
    ) {
        this.workItemManager = workItemManager;
        this.streamingApi = new StreamingApi(apiConfig);
        this.projectContextProvider = projectContextProvider;
        this.authProvider = authProvider;
    }

    private async getWorkItemContext(): Promise<WorkItemContext> {
        const context : WorkItemContext = {
            title: await this.workItemManager.getTitleField(),
            description: await this.workItemManager.getDescriptionField(),
            acceptanceCriteria: await this.workItemManager.getAcceptanceCriteriaField(),
            parentContext: await this.workItemManager.getParentWorkItem(),
            comments: await this.workItemManager.getComments(),
        };

        console.debug("Work item context", context);
        
        return context;
    }

    private async isInputValid(streamingSession: StreamingSession): Promise<boolean> {
        if (this.validate) {
            const validationResult = await this.validate();
            if (!validationResult.isValid) {
                streamingSession.setError(validationResult.error ?? { message: "Input validation failed" });
                return false;
            }
        }

        return true;
    }

    public setValidationLogic(logic: () => Promise<ValidationResult>): void {
        this.validate = logic;
    }

    public async analyzeStream(streamingSession: StreamingSession): Promise<void> {
        if (! await this.isInputValid(streamingSession)) return;

        return await this.callStream("analyze",
            this.streamingApi.streamAnalyzeWorkItem({
                xAccessTokenType: this.authProvider.accessTokenType,
                xOggUserEmail: await this.authProvider.getUserEmail(),
                xWorkItemId: await this.workItemManager.getId(),
                oggRequestContext: {
                    workItem: await this.getWorkItemContext(),
                    project: await this.projectContextProvider.getProjectContext(),
                }
            },
                streamingSession.getRequestParameters()),
            streamingSession
        );
    }

    public async makeSuggestionStream(streamingSession: StreamingSession): Promise<void> {
        if (! await this.isInputValid(streamingSession)) return;

        return this.callStream("suggest",
            this.streamingApi.streamMakeSuggestionBasedOnWorkItem({
                xAccessTokenType: this.authProvider.accessTokenType,
                xOggUserEmail: await this.authProvider.getUserEmail(),
                xWorkItemId: await this.workItemManager.getId(),
                oggRequestContext: {
                    workItem: await this.getWorkItemContext(),
                    project: await this.projectContextProvider.getProjectContext(),
                }
            },
                streamingSession.getRequestParameters()),
            streamingSession
        );
    }

    public async askSherpaStream(streamingSession: StreamingSession): Promise<void> {
        if (! await this.isInputValid(streamingSession)) return;

        return this.callStream("sherpa",
            this.streamingApi.streamSherpa({
                xAccessTokenType: this.authProvider.accessTokenType,
                xOggUserEmail: await this.authProvider.getUserEmail(),
                xWorkItemId: await this.workItemManager.getId(),
                oggRequestContext: {
                    workItem: await this.getWorkItemContext(),
                    project: await this.projectContextProvider.getProjectContext(),
                }
            },
                streamingSession.getRequestParameters()),
            streamingSession
        );
    }

    public async callRaterStream(streamingSession: StreamingSession): Promise<void> {
        if (! await this.isInputValid(streamingSession)) return;

        return this.callStream("rater",
            this.streamingApi.streamRater({
                xAccessTokenType: this.authProvider.accessTokenType,
                xOggUserEmail: await this.authProvider.getUserEmail(),
                xWorkItemId: await this.workItemManager.getId(),
                oggRequestContext: {
                    workItem: await this.getWorkItemContext(),
                    project: await this.projectContextProvider.getProjectContext(),
                }
            },
                streamingSession.getRequestParameters()),
            streamingSession
        );
    }

    public async callScribeStream(streamingSession: StreamingSession): Promise<void> {
        if (! await this.isInputValid(streamingSession)) return;

        const workItemContext = await this.getWorkItemContext();
        workItemContext.meetingTranscription = await this.workItemManager.getMeetingTranscription();

        return this.callStream("scribe",
            this.streamingApi.streamScribe({
                xAccessTokenType: this.authProvider.accessTokenType,
                xOggUserEmail: await this.authProvider.getUserEmail(),
                xWorkItemId: await this.workItemManager.getId(),
                oggRequestContext: {
                    workItem: workItemContext,
                    project: await this.projectContextProvider.getProjectContext(),
                }
            },
                streamingSession.getRequestParameters()),
            streamingSession
        );
    }

    public async applySuggestions(suggestion: Suggestion) {
        await this.workItemManager.setTitleField(suggestion.workItemTitle);
        await this.workItemManager.setDescriptionField(suggestion.workItemDescription);
        await this.workItemManager.setAcceptanceCriteriaField(suggestion.workItemAcceptanceCriteria);
    }

    public async saveRating(rating: Rating) {
        await this.workItemManager.saveRating(rating);
    }

    private async callStream(purpose: string, streamingPromise: Promise<string>, streamingSession: StreamingSession) {
        console.log(`sending request to Ogg to ${purpose}`);

        try {
            const response = await streamingPromise;
            streamingSession.signalEnd();
            console.log("Response from Ogg API: ", response);
        } catch (ex) {
            console.error(ex);
            // ignore errors caused by HTTP request being aborted
            if (ex?.cause?.code == DOMException.ABORT_ERR) return;

            let errorMessage = `An error has occurred while calling Ogg: ${ex}`;

            const response: Response = ex?.response;
            if (!!response) {
                try {
                    var body = await response.json();
                    errorMessage = body.message;
                }
                catch {
                    console.error("Content is not JSON");
                }
            }

            streamingSession.setError({ message: errorMessage});
        }
    }
}

export { OggService as OggFieldService, WorkItemContext, onStreamUpdate }